// content/bosses/boss5.ts — BOSS 5: THE HOARD (DESIGN §5, Zone 5).
//
//   "Verb: deny it food — your loot is its healing."
//
// A serpent of fused coin and crownwork in a treasury littered with gold. It
// does not chase you. It goes for the money — routes to the nearest pile, opens
// a mouth the size of a doorway and takes 1.2 telegraphed seconds to swallow it,
// and every pile it gets down is +4% of its health back and three more hits that
// will glance off the gilded plates growing along its body.
//
// You can beat it to the pile. Standing on one and holding E banks it in 0.6
// seconds — six tenths of a second in the open, in a room with a serpent in it,
// which is the whole fight in one input. It spawns Coinlings; Coinlings die into
// more floor gold; the fight grows its own temptation. In P3 the chandeliers
// come down, and each one is a bigger meal than anything already on the floor.
//
// Everything it ate is on its body when it dies, and it all comes back out.
//
// Every literal comes from data/zones/zone5.ts (ZONE5_BOSS). The numbers written
// in this file are drawing geometry and nothing else.
//
// ---------------------------------------------------------------------------
// FLAGGED IMPLEMENTATION
//
// (a) THE HEAD IS THE TARGET. Every shared hit path tests one circle —
//     boss.pos against boss.radius. Content cannot add hitboxes without editing
//     shared code, so the coils are hazard and the head is the fight. Stated in
//     the data file too, because it is a design position: to hurt it you have to
//     stand where it bites.
//
// (b) THE PILES ARE THE BOSS'S OWN. Floor gold here is not an engine pickup —
//     it must be un-grabbable by walking (DESIGN: grabbing is a *channel*), it
//     must be eatable by something that is not the player, and it must survive
//     the boss dying mid-swallow. All three are impossible through RunCtx's
//     pickup API, so the fight owns the piles, draws them, and converts them
//     into ordinary gold pickups at the moment of death.
//
// (c) COINLING DEATHS ARE TRACKED BY TAG, NOT BY REFERENCE. Enemy bodies are
//     pooled and recycled; a reference held across frames can quietly become a
//     different enemy. Each escort is stamped with a unique id in its tags
//     (cleared by EnemyEnt.reset on reuse), so "the body carrying id 7 is no
//     longer on the floor" is a fact that cannot be spoofed by the pool.
// ---------------------------------------------------------------------------

import type { Draw, RunCtx, Vec } from '../../types'
import { Boss, registerBoss } from '../../game/boss'
import { bossDefeated, registerBossArena } from '../../game/zones'
import { TILE } from '../../game/world'
import { ZONE5_BOSS, ZONE5_BOSS_ARENA, ZONE5_COLORS } from '../../data/zones/zone5'
import { emit } from '../../engine/particles'
import { fx } from '../../engine/fx'
import { PixelArt, drawSprite, registerPainter, registerRecipe, shade } from '../../engine/sprites'
import { isDown } from '../../engine/input'

const D = ZONE5_BOSS
const C = ZONE5_COLORS
const TAU = Math.PI * 2
const RD = Math.round

const tileCenter = (t: { tx: number; ty: number }): Vec => ({
  x: t.tx * TILE + TILE / 2,
  y: t.ty * TILE + TILE / 2,
})

/** Per-phase array read, clamped — phases are 1-based. */
function byPhase<T>(arr: readonly T[], phase: number): T {
  return arr[Math.max(0, Math.min(arr.length - 1, phase - 1))]
}

function num(v: unknown, d: number): number {
  return typeof v === 'number' ? v : d
}

const posScratch: Vec = { x: 0, y: 0 }
const textAt: Vec = { x: 0, y: 0 }

// ---------------------------------------------------------------- structures

interface FloorPile {
  x: number
  y: number
  gold: number
  alive: boolean
  /** > 0 while the Hoard has its mouth around this one */
  eaten: number
  bob: number
  /** chandelier wreckage reads bigger and brighter than ordinary floor gold */
  grand: boolean
}

interface Segment {
  x: number
  y: number
}

interface Escort {
  eid: number
  x: number
  y: number
  active: boolean
}

interface Chandelier {
  x: number
  y: number
  state: number // 0 hanging, 1 falling (telegraph), 2 fallen
  t: number
}

type Stage = 'cruise' | 'wind' | 'lunge' | 'recover' | 'gulp' | 'spray'

class Hoard extends Boss {
  readonly id = D.id
  /** read structurally by game/hud.ts for the boss bar */
  readonly name = D.name
  readonly phaseTicks: readonly number[] = D.phases

  private built = false
  private t = 0
  private stage: Stage = 'cruise'
  private stageT = 0
  private dying = false

  // --- body
  private heading = 0
  private readonly segs: Segment[] = []
  private touchCd = 0

  // --- the meal
  readonly piles: FloorPile[] = []
  private target = -1
  private gulpT = 0
  /** hits that will glance off the gilded plates */
  plated = 0
  /** every coin it has swallowed, owed back at death */
  banked = 0
  private meals = 0
  /** D-032: the meal is contestable — hits/raw damage landed during this gulp */
  private gulpHits = 0
  private gulpRaw = 0

  // --- D-032: the coils answer wasted fire, and the fight teaches its target
  private headHits = 0
  private glances = 0
  private saidArmour = false
  private glanceFxT = 0
  private glanceSfxT = 0

  // --- the Warden's channel
  private grabIndex = -1
  private grabT = 0
  private grabX = 0
  private grabY = 0
  private saidGrab = false

  // --- attacks
  private biteT = 0
  private sprayT = 0
  private summonT = 0
  private escortT = 0
  private lungeVx = 0
  private lungeVy = 0
  private lungeHit = false
  private glintT = 0

  // --- escorts, tracked by tag (see the flagged note)
  private readonly escorts: Escort[] = []
  private escortSeq = 0

  // --- P3
  private readonly chandeliers: Chandelier[] = []
  private chandT = 0
  private chandCursor = 0
  private sweepT = 0
  private sweepState = 0 // 0 idle, 1 telegraph, 2 live
  private sweepSide = 0 // -1 west half, +1 east half
  private sweepHit = false

  private hurtT = 0
  private pingT = 0
  private tookDamage = false
  private lastHp = -1

  constructor() {
    super(D.hp)
    this.radius = D.radius
    const c = tileCenter(D.startTile)
    this.pos.x = c.x
    this.pos.y = c.y
    this.heading = Math.PI / 2
    // laid out in a shallow S at rest: a serpent that spawns as a straight line
    // of discs reads as a stack of coins until it moves
    for (let i = 0; i < D.body.segments; i++) {
      const k = (i + 1) * D.body.spacing
      this.segs.push({ x: c.x + Math.sin(i * 0.55) * 22, y: c.y + k })
    }
    for (let i = 0; i < 16; i++) this.escorts.push({ eid: 0, x: 0, y: 0, active: false })
  }

  // ------------------------------------------------------------------ build

  private build(ctx: RunCtx): void {
    this.built = true
    this.lastHp = ctx.player.hp
    this.biteT = byPhase(D.bite.interval, 1) * 0.8
    this.summonT = byPhase(D.summon.interval, 1) * 0.5
    this.sprayT = byPhase(D.spray.interval, 2)
    this.escortT = D.escort.interval

    for (const t of D.piles.tiles) {
      const c = tileCenter(t)
      this.piles.push({ x: c.x, y: c.y, gold: D.piles.gold, alive: true, eaten: 0, bob: (t.tx + t.ty) * 0.7, grand: false })
    }
    for (const t of D.chandeliers.tiles) {
      const c = tileCenter(t)
      this.chandeliers.push({ x: c.x, y: c.y, state: 0, t: 0 })
    }

    ctx.bark('THE HOARD. It is not hungry for you.')
    ctx.sfx('door')
    ctx.sfx('death')
    ctx.shake(D.intro.shake, D.intro.shakeSeconds)
  }

  // ----------------------------------------------------------------- damage

  /**
   * THE GILDED PLATE. Each meal buys three hits that land as one point of
   * damage. The stack is capped in data so a well-fed Hoard is slow to kill,
   * never impossible — and every glance is loud, because a player who cannot
   * tell "armoured" from "immune" will stop attacking.
   */
  takeDamage(n: number): void {
    if (!this.alive || n <= 0) return
    this.headHits++
    // D-032: a swallow is contestable by force. Count every landed attempt —
    // plated or not — and the RAW damage behind it, so every form can contest:
    // the bow reaches the hit count, the maul reaches the raw threshold.
    if (this.stage === 'gulp') {
      this.gulpHits++
      this.gulpRaw += n
    }
    if (this.plated > 0) {
      this.plated--
      super.takeDamage(D.eat.platedDamage)
      if (this.pingT <= 0) {
        this.pingT = 0.12
        this.ctx?.sfx('ui')
        fx.floatText('PLATED', this.pos.x, this.pos.y - 34, C.plate, 12)
        emit(this.pos.x, this.pos.y, { count: 7, color: C.plate, speed: 150, life: 0.22, size: 3, shape: 1 })
      }
    } else {
      const before = this.hp
      super.takeDamage(n)
      if (this.hp < before) this.hurtT = 0.12
    }
    if (
      this.stage === 'gulp' &&
      this.ctx &&
      (this.gulpHits >= D.eat.interruptHits || this.gulpRaw >= D.eat.interruptDamage)
    ) {
      this.breakGulp(this.ctx, true)
    }
  }

  /**
   * D-032: a friendly shot that missed the head (game.ts calls this for every
   * live friendly projectile that failed the head check). If it is crossing
   * the coils, answer it — a glance spark and a tink — so wasted fire reads as
   * "armoured", never as nothing. Throttled: sustained fire sparks, not strobes.
   */
  shotGlance(x: number, y: number, radius: number): void {
    if (!this.alive || this.dying || this.glanceFxT > 0) return
    for (let i = 0; i < this.segs.length; i++) {
      const s = this.segs[i]
      if (Math.hypot(s.x - x, s.y - y) > D.body.radius + radius + 2) continue
      this.glanceFxT = 0.08
      this.glances++
      emit(x, y, { count: 5, color: C.plate, speed: 130, life: 0.18, size: 2, shape: 1 })
      if (this.glanceSfxT <= 0) {
        this.glanceSfxT = 0.3
        this.ctx?.sfx('ui')
      }
      return
    }
  }

  // ----------------------------------------------------------------- update

  update(ctx: RunCtx, dt: number): void {
    this.tick(ctx, dt)
    if (!this.built) this.build(ctx)
    this.t += dt
    this.stageT += dt
    if (this.hurtT > 0) this.hurtT -= dt
    if (this.pingT > 0) this.pingT -= dt
    if (this.glintT > 0) this.glintT -= dt
    if (this.touchCd > 0) this.touchCd -= dt
    if (this.glanceFxT > 0) this.glanceFxT -= dt
    if (this.glanceSfxT > 0) this.glanceSfxT -= dt
    // D-032: the fight teaches its target. A player whose fire keeps glancing
    // off the coils — or who has landed nothing well into the fight — gets the
    // lesson said out loud, once.
    if (!this.saidArmour && this.headHits === 0 && (this.glances >= 8 || this.t > 12)) {
      this.saidArmour = true
      ctx.bark('The coin is armour. The crowned head is the way in.')
    }
    if (this.dying) return

    // no-hit watch: any drop in the Warden's health spends the clean kill.
    if (this.lastHp >= 0 && ctx.player.hp < this.lastHp) this.tookDamage = true
    this.lastHp = ctx.player.hp

    if (this.hp <= 0) {
      this.die(ctx)
      return
    }

    const frac = this.healthFraction
    if (frac <= D.phases[1]) this.setPhase(3)
    else if (frac <= D.phases[0]) this.setPhase(2)

    this.updateEscorts(ctx)
    this.updateGrab(ctx, dt)
    this.updateMove(ctx, dt)
    this.updateBody(ctx, dt)
    this.updateBite(ctx, dt)
    this.updateSummon(ctx, dt)
    if (this.phase >= 2) {
      this.updateSpray(ctx, dt)
      this.updateEscortSummon(ctx, dt)
    }
    if (this.phase >= 3) {
      this.updateChandeliers(ctx, dt)
      this.updateSweep(ctx, dt)
    }
  }

  protected onPhase(phase: number): void {
    const ctx = this.ctx
    if (!ctx) return
    if (phase === 2) {
      ctx.bark('It stops pretending you matter. Coin-spray, and something in armour.')
      ctx.sfx('generator-die')
      ctx.shake(6, 0.5)
      this.sprayT = byPhase(D.spray.interval, 2) * 0.4
      this.escortT = 0
    } else if (phase === 3) {
      ctx.bark('It looks up. The chandeliers are gold too.')
      ctx.sfx('death')
      ctx.shake(8, 0.7)
      this.chandT = 1.2
      // Belt-and-braces beside the monotonic-phase guard (game/boss.ts): a
      // phase entry must never leave a sweep half-alive with its clock rewound.
      this.sweepState = 0
      this.sweepT = D.sweep.interval * 0.5
    }
  }

  // -------------------------------------------------------------- the piles

  private livePiles(): number {
    let n = 0
    for (let i = 0; i < this.piles.length; i++) if (this.piles[i].alive) n++
    return n
  }

  /**
   * What it is going for. Ordinary piles are chosen by distance; a fallen
   * chandelier outweighs everything, which is what makes the P3 windows a race
   * rather than a coincidence.
   */
  private pickTarget(): number {
    let best = -1
    let bestScore = Infinity
    for (let i = 0; i < this.piles.length; i++) {
      const p = this.piles[i]
      if (!p.alive) continue
      const d = Math.hypot(p.x - this.pos.x, p.y - this.pos.y)
      const score = p.grand ? d * 0.25 : d
      if (score < bestScore) {
        bestScore = score
        best = i
      }
    }
    return best
  }

  private addPile(x: number, y: number, gold: number, grand = false): void {
    // reuse a dead slot before growing the array — the fight can run long
    for (let i = 0; i < this.piles.length; i++) {
      const p = this.piles[i]
      if (p.alive) continue
      p.x = x
      p.y = y
      p.gold = gold
      p.alive = true
      p.eaten = 0
      p.grand = grand
      p.bob = (x + y) * 0.01
      return
    }
    this.piles.push({ x, y, gold, alive: true, eaten: 0, bob: (x + y) * 0.01, grand })
  }

  // --------------------------------------------------------- the Warden's E

  /**
   * THE CHANNEL. 0.6 seconds, standing still, in the open. Breaking it is easy
   * — release E, or drift more than a stride — and that is the point: this is
   * the input the fight is built around, and it has to cost something.
   */
  private updateGrab(ctx: RunCtx, dt: number): void {
    const held = isDown('interact')
    const px = ctx.player.pos.x
    const py = ctx.player.pos.y

    if (this.grabIndex >= 0) {
      const p = this.piles[this.grabIndex]
      const drift = Math.hypot(px - this.grabX, py - this.grabY)
      const inReach = p && p.alive && Math.hypot(p.x - px, p.y - py) <= D.grab.reach
      if (!held || !inReach || drift > D.grab.moveSlack) {
        this.grabIndex = -1
        this.grabT = 0
        return
      }
      this.grabT += dt
      if (ctx.rng() < 0.5) {
        emit(p.x, p.y, { count: 1, color: C.goldHot, speed: 60, life: 0.3, size: 2, grav: -60, shape: 1 })
      }
      if (this.grabT >= D.grab.seconds) {
        this.grabIndex = -1
        this.grabT = 0
        p.alive = false
        ctx.addGold(p.gold)
        ctx.sfx('pickup')
        ctx.sfx('ui')
        fx.floatText(`+${p.gold}`, p.x, p.y - 16, C.goldHot, 15)
        emit(p.x, p.y, {
          count: 26,
          color: C.coin,
          speed: 190,
          speedVar: 120,
          life: 0.6,
          lifeVar: 0.25,
          size: 3,
          grav: 260,
          shape: 1,
        })
        // taken out of its mouth
        if (this.stage === 'gulp' && this.target === this.piles.indexOf(p)) this.breakGulp(ctx)
        if (!this.saidGrab) {
          this.saidGrab = true
          ctx.bark('Banked. That is one it does not get.')
        }
      }
      return
    }

    if (!held) return
    let best = -1
    let bestD = D.grab.reach * D.grab.reach
    for (let i = 0; i < this.piles.length; i++) {
      const p = this.piles[i]
      if (!p.alive) continue
      const d = (p.x - px) * (p.x - px) + (p.y - py) * (p.y - py)
      if (d < bestD) {
        bestD = d
        best = i
      }
    }
    if (best < 0) return
    this.grabIndex = best
    this.grabT = 0
    this.grabX = px
    this.grabY = py
    ctx.sfx('ui')
  }

  // ------------------------------------------------------------- the moving

  private updateMove(ctx: RunCtx, dt: number): void {
    if (this.stage === 'wind' || this.stage === 'lunge' || this.stage === 'spray') return

    if (this.stage === 'gulp') {
      this.updateGulp(ctx, dt)
      return
    }
    if (this.stage === 'recover') {
      if (this.stageT > D.bite.recover) this.setStage('cruise')
      return
    }

    // pick the meal, or the Warden if the floor is bare
    if (this.target < 0 || !this.piles[this.target]?.alive) this.target = this.pickTarget()
    const p = this.target >= 0 ? this.piles[this.target] : null
    const tx = p ? p.x : ctx.player.pos.x
    const ty = p ? p.y : ctx.player.pos.y

    const want = Math.atan2(ty - this.pos.y, tx - this.pos.x)
    const turn = byPhase(D.move.turnRate, this.phase) * dt
    let d = want - this.heading
    while (d > Math.PI) d -= TAU
    while (d < -Math.PI) d += TAU
    this.heading += Math.max(-turn, Math.min(turn, d))

    const sp = byPhase(D.move.speed, this.phase) * dt
    this.tryMove(ctx, Math.cos(this.heading) * sp, Math.sin(this.heading) * sp)

    if (p && Math.hypot(p.x - this.pos.x, p.y - this.pos.y) <= D.eat.reach) this.beginGulp(ctx)
  }

  /** Axis-separated move with a leading-edge wall check: the treasury is open. */
  private tryMove(ctx: RunCtx, dx: number, dy: number): void {
    const r = this.radius * 0.6
    if (dx !== 0) {
      const nx = this.pos.x + dx
      if (!ctx.isWall(Math.floor((nx + Math.sign(dx) * r) / TILE), Math.floor(this.pos.y / TILE))) {
        this.pos.x = nx
      } else {
        this.heading += 0.6
      }
    }
    if (dy !== 0) {
      const ny = this.pos.y + dy
      if (!ctx.isWall(Math.floor(this.pos.x / TILE), Math.floor((ny + Math.sign(dy) * r) / TILE))) {
        this.pos.y = ny
      } else {
        this.heading += 0.6
      }
    }
  }

  /** The train. Each coil holds station on the one ahead of it. */
  private updateBody(ctx: RunCtx, dt: number): void {
    let px = this.pos.x
    let py = this.pos.y
    const gap = D.body.spacing
    for (let i = 0; i < this.segs.length; i++) {
      const s = this.segs[i]
      const dx = s.x - px
      const dy = s.y - py
      const d = Math.hypot(dx, dy)
      if (d > gap) {
        const k = gap / d
        s.x = px + dx * k
        s.y = py + dy * k
      }
      px = s.x
      py = s.y
    }

    // the coils hurt to stand in, on a cooldown so a brush is not a shredder
    if (this.touchCd > 0) return
    const p = ctx.player.pos
    for (let i = 0; i < this.segs.length; i++) {
      const s = this.segs[i]
      if (Math.hypot(s.x - p.x, s.y - p.y) > D.body.radius + 10) continue
      this.touchCd = D.body.touchGap
      ctx.damagePlayer(D.body.touchDamage, D.id)
      emit(p.x, p.y, { count: 8, color: C.gold, speed: 140, life: 0.3, size: 3 })
      return
    }
  }

  // -------------------------------------------------------------- the meal

  private beginGulp(ctx: RunCtx): void {
    if (this.stage === 'gulp') return
    this.setStage('gulp')
    this.gulpT = D.eat.gulpSeconds
    this.gulpHits = 0
    this.gulpRaw = 0
    this.telegraph('gulp', D.eat.gulpSeconds)
    const p = this.piles[this.target]
    if (p) p.eaten = D.eat.gulpSeconds
    ctx.sfx('telegraph')
    ctx.bark('It has its mouth around one. Take it or lose it.')
  }

  private breakGulp(ctx: RunCtx, struck = false): void {
    this.setStage('cruise')
    this.gulpT = 0
    this.gulpHits = 0
    this.gulpRaw = 0
    // release the pile's swallow clock — the meal did not land
    for (let i = 0; i < this.piles.length; i++) this.piles[i].eaten = 0
    this.target = -1
    ctx.sfx('ui')
    ctx.bark(struck ? 'Struck mid-swallow. It gets nothing.' : 'Snapped shut on nothing.')
  }

  private updateGulp(ctx: RunCtx, dt: number): void {
    const p = this.target >= 0 ? this.piles[this.target] : null
    if (!p || !p.alive) {
      this.breakGulp(ctx)
      return
    }
    this.gulpT -= dt
    p.eaten = this.gulpT
    // it stops dead to swallow: this is the window
    emit(p.x, p.y - 6, { count: 1, color: C.coin, speed: 70, life: 0.35, size: 3, grav: -120, shape: 1 })
    if (this.gulpT > 0) return

    // THE MEAL LANDS. Nothing above this line consumes the pile, which is why a
    // Hoard killed mid-swallow leaves it on the floor.
    p.alive = false
    p.eaten = 0
    this.banked += p.gold
    this.meals++
    const heal = Math.max(1, Math.round(this.maxHp * D.eat.healFraction))
    this.hp = Math.min(this.maxHp, this.hp + heal)
    this.plated = Math.min(D.eat.plateCap, this.plated + D.eat.plateHits)
    this.target = -1
    this.setStage('cruise')

    ctx.sfx('potion')
    ctx.sfx('generator-die')
    ctx.shake(5, 0.4)
    fx.floatText(`+${heal}`, this.pos.x, this.pos.y - 40, '#6fe08a', 16)
    fx.floatText('PLATED', this.pos.x, this.pos.y - 22, C.plate, 13)
    emit(this.pos.x, this.pos.y, {
      count: 24,
      color: C.plate,
      speed: 170,
      speedVar: 110,
      life: 0.5,
      lifeVar: 0.2,
      size: 4,
      shape: 1,
    })
    if (this.meals === 1) ctx.bark('It ate. That was your gold, and now it is armour.')
  }

  // ------------------------------------------------------------- the biting

  private setStage(s: Stage): void {
    this.stage = s
    this.stageT = 0
  }

  private updateBite(ctx: RunCtx, dt: number): void {
    if (this.stage === 'wind') {
      if (this.stageT >= this.windupLeft) {
        const dx = ctx.player.pos.x - this.pos.x
        const dy = ctx.player.pos.y - this.pos.y
        const l = Math.hypot(dx, dy) || 1
        this.lungeVx = (dx / l) * D.bite.dashSpeed
        this.lungeVy = (dy / l) * D.bite.dashSpeed
        this.heading = Math.atan2(dy, dx)
        this.lungeHit = false
        this.setStage('lunge')
        ctx.sfx('swing')
      }
      return
    }

    if (this.stage === 'lunge') {
      this.tryMove(ctx, this.lungeVx * dt, this.lungeVy * dt)
      if (!this.lungeHit) {
        const d = Math.hypot(ctx.player.pos.x - this.pos.x, ctx.player.pos.y - this.pos.y)
        if (d < D.bite.hitRange) {
          this.lungeHit = true
          ctx.damagePlayer(D.bite.damage, D.id)
          ctx.shake(6, 0.3)
          emit(ctx.player.pos.x, ctx.player.pos.y, { count: 14, color: C.cursed, speed: 200, life: 0.3, size: 4 })
        }
      }
      if (this.stageT > D.bite.dashTime) this.setStage('recover')
      return
    }

    if (this.stage !== 'cruise') return
    this.biteT -= dt
    if (this.biteT > 0) return
    const d = Math.hypot(ctx.player.pos.x - this.pos.x, ctx.player.pos.y - this.pos.y)
    if (d > D.bite.range) return
    this.biteT = byPhase(D.bite.interval, this.phase)
    this.windupLeft = byPhase(D.bite.windup, this.phase)
    this.glintT = this.windupLeft
    this.telegraph('bite', this.windupLeft)
    this.setStage('wind')
  }

  private windupLeft = 0.5

  // -------------------------------------------------------------- the spray

  private updateSpray(ctx: RunCtx, dt: number): void {
    if (this.stage === 'spray') {
      if (this.stageT < D.spray.windup) return
      const base = Math.atan2(ctx.player.pos.y - this.pos.y, ctx.player.pos.x - this.pos.x)
      const spread = D.spray.spreadDeg * (Math.PI / 180)
      for (let i = 0; i < D.spray.count; i++) {
        const a = base + (i / (D.spray.count - 1) - 0.5) * spread
        posScratch.x = this.pos.x + Math.cos(a) * (this.radius + 6)
        posScratch.y = this.pos.y + Math.sin(a) * (this.radius + 6)
        dirScratch.x = Math.cos(a)
        dirScratch.y = Math.sin(a)
        ctx.spawnProjectile(D.spray.projectile, posScratch, dirScratch, false)
      }
      ctx.sfx('shoot')
      ctx.shake(3, 0.2)
      emit(this.pos.x, this.pos.y, { count: 18, color: C.coin, speed: 220, life: 0.4, size: 3, shape: 1 })
      this.setStage('cruise')
      return
    }
    if (this.stage !== 'cruise') return
    this.sprayT -= dt
    if (this.sprayT > 0) return
    this.sprayT = byPhase(D.spray.interval, this.phase)
    this.telegraph('spray', D.spray.windup)
    this.setStage('spray')
  }

  // ------------------------------------------------------------ the escorts

  private updateSummon(ctx: RunCtx, dt: number): void {
    this.summonT -= dt
    if (this.summonT > 0) return
    this.summonT = byPhase(D.summon.interval, this.phase)
    let live = 0
    const list = ctx.enemies
    for (let i = 0; i < list.length; i++) if (list[i].alive) live++
    if (live >= D.summon.liveCap) return
    for (let i = 0; i < D.summon.count; i++) {
      const a = (i / D.summon.count) * TAU + this.t
      posScratch.x = this.pos.x + Math.cos(a) * D.summon.spread
      posScratch.y = this.pos.y + Math.sin(a) * D.summon.spread
      if (ctx.isWall(Math.floor(posScratch.x / TILE), Math.floor(posScratch.y / TILE))) {
        posScratch.x = this.pos.x
        posScratch.y = this.pos.y + D.summon.spread * 0.5
      }
      const e = ctx.spawnEnemy(D.summon.defId, posScratch, { elite: null })
      if (!e.alive) continue
      const eid = ++this.escortSeq
      e.tags.z5eid = eid
      for (let s = 0; s < this.escorts.length; s++) {
        const slot = this.escorts[s]
        if (slot.active) continue
        slot.active = true
        slot.eid = eid
        slot.x = posScratch.x
        slot.y = posScratch.y
        break
      }
    }
    ctx.sfx('generator-die')
  }

  private updateEscortSummon(ctx: RunCtx, dt: number): void {
    this.escortT -= dt
    if (this.escortT > 0) return
    this.escortT = D.escort.interval
    const list = ctx.enemies
    for (let i = 0; i < list.length; i++) {
      if (list[i].alive && list[i].def.id === D.escort.defId) return
    }
    for (let i = 0; i < D.escort.count; i++) {
      posScratch.x = this.pos.x + (i === 0 ? -1 : 1) * 150
      posScratch.y = this.pos.y + 90
      if (ctx.isWall(Math.floor(posScratch.x / TILE), Math.floor(posScratch.y / TILE))) {
        posScratch.x = this.pos.x
        posScratch.y = this.pos.y + 120
      }
      ctx.spawnEnemy(D.escort.defId, posScratch, { elite: null })
    }
    ctx.sfx('door')
    ctx.bark('An escort in plate. It only faces one way — remember that.')
  }

  /**
   * A tracked Coinling that is no longer on the floor died, and DESIGN says a
   * dead Coinling is more floor gold. The fight grows its own temptation.
   */
  private updateEscorts(ctx: RunCtx): void {
    const list = ctx.enemies
    for (let s = 0; s < this.escorts.length; s++) {
      const slot = this.escorts[s]
      if (!slot.active) continue
      let seen = false
      for (let i = 0; i < list.length; i++) {
        const e = list[i]
        if (!e.alive || num(e.tags.z5eid, -1) !== slot.eid) continue
        slot.x = e.pos.x
        slot.y = e.pos.y
        seen = true
        break
      }
      if (seen) continue
      slot.active = false
      this.addPile(slot.x, slot.y, D.summon.pileGold, false)
      emit(slot.x, slot.y, { count: 12, color: C.coin, speed: 150, life: 0.4, size: 3, grav: 240, shape: 1 })
    }
  }

  // --------------------------------------------------------- P3: the ceiling

  private updateChandeliers(ctx: RunCtx, dt: number): void {
    // one at a time, on the gap, until all three are down
    if (this.chandCursor < this.chandeliers.length) {
      this.chandT -= dt
      if (this.chandT <= 0) {
        const c = this.chandeliers[this.chandCursor]
        if (c.state === 0) {
          c.state = 1
          c.t = D.chandeliers.telegraph
          this.chandT = D.chandeliers.gap
          this.chandCursor++
          ctx.sfx('telegraph')
          ctx.shake(2.5, 0.4)
          ctx.bark('Above you. Move, then take it before it does.')
        }
      }
    }
    for (let i = 0; i < this.chandeliers.length; i++) {
      const c = this.chandeliers[i]
      if (c.state !== 1) continue
      c.t -= dt
      if (c.t > 0) continue
      c.state = 2
      const d = Math.hypot(ctx.player.pos.x - c.x, ctx.player.pos.y - c.y)
      if (d <= D.chandeliers.crashRadius) ctx.damagePlayer(D.chandeliers.crashDamage, D.id + '-chandelier')
      ctx.sfx('generator-die')
      ctx.sfx('death')
      ctx.shake(10, 0.7)
      ctx.hitstop(0.06)
      emit(c.x, c.y, {
        count: 40,
        color: C.goldHot,
        speed: 260,
        speedVar: 180,
        life: 0.8,
        lifeVar: 0.35,
        size: 4,
        grav: 320,
        shape: 1,
      })
      this.addPile(c.x, c.y, D.chandeliers.gold, true)
      this.target = -1
    }
  }

  // ---------------------------------------------------------- P3: the sweep

  private updateSweep(ctx: RunCtx, dt: number): void {
    const midX = (ZONE5_BOSS_ARENA.map[0].length * TILE) / 2
    if (this.sweepState === 0) {
      this.sweepT -= dt
      if (this.sweepT > 0) return
      this.sweepState = 1
      this.sweepT = D.sweep.telegraph
      this.sweepSide = ctx.player.pos.x < midX ? -1 : 1
      this.sweepHit = false
      this.telegraph('sweep', D.sweep.telegraph)
      ctx.sfx('telegraph')
      ctx.bark('It gathers half the room into a coil. Dash it.')
      return
    }
    if (this.sweepState === 1) {
      this.sweepT -= dt
      if (this.sweepT > 0) return
      this.sweepState = 2
      this.sweepT = D.sweep.seconds
      ctx.sfx('swing')
      ctx.shake(7, 0.5)
      return
    }
    this.sweepT -= dt
    if (!this.sweepHit) {
      const inBand = this.sweepSide < 0 ? ctx.player.pos.x < midX : ctx.player.pos.x > midX
      if (inBand) {
        this.sweepHit = true
        ctx.damagePlayer(D.sweep.damage, D.id + '-coil')
        ctx.pushPlayer(this.sweepSide * D.sweep.push, 0)
      }
    }
    if (this.sweepT <= 0) {
      this.sweepState = 0
      this.sweepT = D.sweep.interval
    }
  }

  // ------------------------------------------------------------------ death

  /**
   * THE GREEDY PAYOUT. Everything it swallowed comes back out of the corpse,
   * and everything still on the floor stops being a channel and becomes ordinary
   * coin you can walk over. The arena should look like a burst till.
   */
  private die(ctx: RunCtx): void {
    this.dying = true
    this.alive = false
    this.hp = 0
    this.grabIndex = -1
    // nothing it started may outlive it: a dead serpent must not leave a live
    // hazard painted across half the arena, or a chandelier hanging in the air
    this.sweepState = 0
    this.stage = 'cruise'
    for (let i = 0; i < this.chandeliers.length; i++) this.chandeliers[i].state = 2
    ctx.hitstop(D.death.hitstop)
    ctx.shake(D.death.shake, D.death.shakeSeconds)
    ctx.sfx('generator-die')
    ctx.sfx('death')
    // the jackpot: a rising sweep under the collapse, because what happens next
    // is a till bursting open
    ctx.sfx('potion')

    for (let i = 0; i < 3; i++) {
      emit(this.pos.x, this.pos.y, {
        count: 40,
        color: i === 0 ? C.goldHot : i === 1 ? C.gold : C.cursed,
        speed: 260 + i * 90,
        speedVar: 200,
        life: 1,
        lifeVar: 0.5,
        size: 5,
        grav: 220,
        drag: 0.88,
      })
    }

    // the escorts go with it, silently — a quiet arena for the payout
    const list = ctx.enemies
    for (let i = list.length - 1; i >= 0; i--) if (list[i].alive) list[i].kill(true)

    // 1) everything it ate, spat back around the body
    let owed = this.banked
    const chunk = 25
    let n = 0
    while (owed > 0 && n < 40) {
      const give = Math.min(chunk, owed)
      owed -= give
      const a = (n / 9) * TAU + this.t
      const r = 40 + (n % 5) * 26
      const x = this.pos.x + Math.cos(a) * r
      const y = this.pos.y + Math.sin(a) * r
      if (!ctx.isWall(Math.floor(x / TILE), Math.floor(y / TILE))) {
        ctx.spawnPickup({ kind: 'gold', pos: { x, y }, value: give })
      } else {
        ctx.spawnPickup({ kind: 'gold', pos: { x: this.pos.x, y: this.pos.y }, value: give })
      }
      n++
    }
    // 2) and every pile it never got to
    for (let i = 0; i < this.piles.length; i++) {
      const p = this.piles[i]
      if (!p.alive) continue
      p.alive = false
      ctx.spawnPickup({ kind: 'gold', pos: { x: p.x, y: p.y }, value: p.gold })
    }
    if (this.banked > 0) {
      fx.floatText(`${this.banked} GOLD`, this.pos.x, this.pos.y - 40, C.goldHot, 20)
      ctx.bark(`Everything it swallowed is on the floor. ${this.banked} gold of it.`)
    } else {
      ctx.bark('It died starving. Nothing of yours ever went down.')
    }

    ctx.featTick('boss5_kill')
    if (!this.tookDamage) {
      ctx.featTick('boss5_nohit')
      ctx.bark('Nothing for the coils.')
    }
    bossDefeated(ctx)
  }

  // ----------------------------------------------------------------- render

  render(d: Draw): void {
    const ctx = d.ctx
    if (this.dying) {
      // the corpse, and nothing else: dimmed, unlit, harmless
      ctx.save()
      ctx.globalAlpha = 0.5
      this.renderBody(ctx)
      this.renderHead(ctx)
      ctx.restore()
      return
    }
    this.renderSweep(ctx)
    this.renderPiles(ctx)
    this.renderChandeliers(ctx)
    this.renderBody(ctx)
    this.renderHead(ctx)
    // the swallow clock goes on TOP of the head — the head is parked over the
    // pile it is eating, and the one thing that must never be hidden by the
    // serpent is how long you have to take the pile off it
    this.renderGulpClock(ctx)
    this.renderGrab(d)
    this.renderTelegraphs(d)
  }

  private renderPiles(ctx: CanvasRenderingContext2D): void {
    for (let i = 0; i < this.piles.length; i++) {
      const p = this.piles[i]
      if (!p.alive) continue
      const pulse = 0.55 + 0.45 * Math.sin(this.t * 3 + p.bob)
      const scale = p.grand ? 1.5 : 1
      ctx.globalAlpha = 0.3
      ctx.fillStyle = '#1a0d08'
      ctx.beginPath()
      ctx.ellipse(p.x, p.y + 8 * scale, 22 * scale, 9 * scale, 0, 0, TAU)
      ctx.fill()
      ctx.globalAlpha = 0.16 + 0.24 * pulse
      ctx.fillStyle = C.goldHot
      ctx.beginPath()
      ctx.ellipse(p.x, p.y + 6, (28 + pulse * 8) * scale, (14 + pulse * 4) * scale, 0, 0, TAU)
      ctx.fill()
      ctx.globalAlpha = 1
      drawSprite(ctx, 'z5_hoardpile', p.x, p.y, { scale })
    }
  }

  /** Mid-swallow: a closing ring, drawn over everything, on the pile at risk. */
  private renderGulpClock(ctx: CanvasRenderingContext2D): void {
    for (let i = 0; i < this.piles.length; i++) {
      const p = this.piles[i]
      if (!p.alive || p.eaten <= 0) continue
      const scale = p.grand ? 1.5 : 1
      const k = 1 - Math.max(0, p.eaten) / D.eat.gulpSeconds
      const r = 36 * scale
      ctx.globalAlpha = 0.5
      ctx.strokeStyle = '#0b0603'
      ctx.lineWidth = 6
      ctx.beginPath()
      ctx.arc(p.x, p.y, r, 0, TAU)
      ctx.stroke()
      ctx.globalAlpha = 0.95
      ctx.strokeStyle = C.cursed
      ctx.lineWidth = 4.5
      ctx.beginPath()
      ctx.arc(p.x, p.y, r, -Math.PI / 2, -Math.PI / 2 + TAU * k)
      ctx.stroke()
      ctx.globalAlpha = 1
    }
  }

  private renderChandeliers(ctx: CanvasRenderingContext2D): void {
    for (let i = 0; i < this.chandeliers.length; i++) {
      const c = this.chandeliers[i]
      if (c.state === 2) continue
      if (c.state === 0) {
        // still hanging: high, a little hazy with distance, and obviously money
        const sway = Math.sin(this.t * 0.9 + c.x * 0.01) * 4
        ctx.globalAlpha = 0.16
        ctx.fillStyle = '#ffe08a'
        ctx.beginPath()
        ctx.ellipse(c.x, c.y, 30, 12, 0, 0, TAU)
        ctx.fill()
        ctx.globalAlpha = 1
        drawSprite(ctx, 'z5_chandelier', c.x + sway, c.y - 30, { scale: 0.9, alpha: 0.8 })
        continue
      }
      const k = 1 - c.t / D.chandeliers.telegraph
      // the marked circle: this is the fall point, and it is also the prize
      ctx.globalAlpha = 0.2 + 0.35 * k
      ctx.fillStyle = C.cursedDeep
      ctx.beginPath()
      ctx.arc(c.x, c.y, D.chandeliers.crashRadius, 0, TAU)
      ctx.fill()
      ctx.globalAlpha = 0.55 + 0.4 * Math.sin(this.t * 22)
      ctx.strokeStyle = C.cursed
      ctx.lineWidth = 4
      ctx.beginPath()
      ctx.arc(c.x, c.y, D.chandeliers.crashRadius * (1 - k * 0.25), 0, TAU)
      ctx.stroke()
      ctx.globalAlpha = 1
      // and the thing itself, coming down
      drawSprite(ctx, 'z5_chandelier', c.x, c.y - 120 * (1 - k), { scale: 0.8 + k * 0.5 })
    }
  }

  private renderSweep(ctx: CanvasRenderingContext2D): void {
    if (this.sweepState === 0) return
    const w = ZONE5_BOSS_ARENA.map[0].length * TILE
    const h = ZONE5_BOSS_ARENA.map.length * TILE
    const midX = w / 2
    const x0 = this.sweepSide < 0 ? 0 : midX
    const live = this.sweepState === 2
    const k = live ? 1 : 1 - this.sweepT / D.sweep.telegraph
    ctx.save()
    ctx.globalAlpha = live ? 0.34 + 0.12 * Math.sin(this.t * 26) : 0.1 + 0.22 * k
    ctx.fillStyle = live ? '#8a1f1f' : C.cursedDeep
    ctx.fillRect(x0, 0, midX, h)
    ctx.globalAlpha = live ? 0.9 : 0.5 + 0.4 * Math.sin(this.t * 18)
    ctx.fillStyle = C.cursed
    ctx.fillRect(this.sweepSide < 0 ? midX - 5 : midX, 0, 5, h)
    if (live) {
      // the coil itself, drawn as a thick arc raking the half
      ctx.globalAlpha = 0.85
      ctx.strokeStyle = C.gold
      ctx.lineWidth = 26
      ctx.beginPath()
      const sweep = 1 - this.sweepT / D.sweep.seconds
      const cx = this.sweepSide < 0 ? midX : midX
      ctx.arc(cx, h / 2, 60 + sweep * (midX - 40), this.sweepSide < 0 ? Math.PI * 0.55 : -Math.PI * 0.45, this.sweepSide < 0 ? Math.PI * 1.45 : Math.PI * 0.45)
      ctx.stroke()
    }
    ctx.restore()
  }

  private renderBody(ctx: CanvasRenderingContext2D): void {
    // tail first so the body overlaps correctly toward the head
    for (let i = this.segs.length - 1; i >= 0; i--) {
      const s = this.segs[i]
      const k = 1 - i / (this.segs.length + 1)
      const r = D.body.radius * (0.55 + 0.45 * k)
      ctx.globalAlpha = 0.3
      ctx.fillStyle = '#1a0d08'
      ctx.beginPath()
      ctx.ellipse(s.x, s.y + r * 0.7, r * 0.95, r * 0.42, 0, 0, TAU)
      ctx.fill()
      ctx.globalAlpha = 1
      drawSprite(ctx, 'z5_hoard_seg', s.x, s.y, { scale: (r * 2) / 30, white: this.hurtT > 0 ? 0.3 : 0 })
      // THE PLATES: one per remaining plated hit, worn from the neck back, so
      // the armour stack is a thing you can count on its body.
      if (i < this.plated) {
        const pulse = 0.7 + 0.3 * Math.sin(this.t * 6 + i)
        ctx.globalAlpha = pulse
        ctx.strokeStyle = C.plate
        ctx.lineWidth = 3.5
        ctx.beginPath()
        ctx.arc(s.x, s.y, r + 3, 0, TAU)
        ctx.stroke()
        ctx.globalAlpha = 0.35 * pulse
        ctx.fillStyle = C.goldHot
        ctx.beginPath()
        ctx.arc(s.x, s.y, r + 3, 0, TAU)
        ctx.fill()
        ctx.globalAlpha = 1
      }
    }
  }

  private renderHead(ctx: CanvasRenderingContext2D): void {
    const gulping = this.stage === 'gulp'
    const open = gulping || this.stage === 'lunge' || this.stage === 'wind'
    ctx.globalAlpha = 0.34
    ctx.fillStyle = '#1a0d08'
    ctx.beginPath()
    ctx.ellipse(this.pos.x, this.pos.y + this.radius * 0.7, this.radius * 0.95, this.radius * 0.4, 0, 0, TAU)
    ctx.fill()
    ctx.globalAlpha = 1
    drawSprite(ctx, open ? 'z5_hoard_head_open' : 'z5_hoard_head', this.pos.x, this.pos.y, {
      rot: this.heading + Math.PI / 2,
      scale: gulping ? 1.12 : 1,
      white: this.hurtT > 0 ? 0.8 : 0,
    })
    if (this.glintT > 0) {
      // the red glint before a bite: small, bright, and always the same tell
      const k = 0.45 + 0.55 * Math.abs(Math.sin(this.t * 26))
      const gx = this.pos.x + Math.cos(this.heading) * (this.radius + 6)
      const gy = this.pos.y + Math.sin(this.heading) * (this.radius + 6)
      ctx.globalAlpha = k
      ctx.fillStyle = C.cursed
      ctx.beginPath()
      ctx.moveTo(gx, gy - 7)
      ctx.lineTo(gx + 5, gy)
      ctx.lineTo(gx, gy + 7)
      ctx.lineTo(gx - 5, gy)
      ctx.closePath()
      ctx.fill()
      ctx.globalAlpha = 0.35 * k
      ctx.beginPath()
      ctx.arc(gx, gy, 15, 0, TAU)
      ctx.fill()
      ctx.globalAlpha = 1
    }
  }

  private renderGrab(d: Draw): void {
    const run = this.ctx
    if (!run) return
    const ctx = d.ctx
    const px = run.player.pos.x
    const py = run.player.pos.y

    if (this.grabIndex >= 0) {
      const k = Math.max(0, Math.min(1, this.grabT / D.grab.seconds))
      const w = 54
      ctx.globalAlpha = 0.85
      ctx.fillStyle = '#00000099'
      ctx.fillRect(px - w / 2 - 1, py - 39, w + 2, 8)
      ctx.fillStyle = C.goldHot
      ctx.fillRect(px - w / 2, py - 38, w * k, 6)
      ctx.globalAlpha = 1
      textAt.x = px
      textAt.y = py - 50
      d.text('TAKING IT', textAt, 11, C.goldHot)
      return
    }
    // otherwise: prompt on the nearest pile, so the input is discoverable
    let best = -1
    let bestD = D.grab.reach * D.grab.reach
    for (let i = 0; i < this.piles.length; i++) {
      const p = this.piles[i]
      if (!p.alive) continue
      const dd = (p.x - px) * (p.x - px) + (p.y - py) * (p.y - py)
      if (dd < bestD) {
        bestD = dd
        best = i
      }
    }
    if (best < 0) return
    textAt.x = this.piles[best].x
    textAt.y = this.piles[best].y - 30
    d.text('HOLD E', textAt, 12, C.goldHot)
  }

  // --------------------------------------------------------------- test seam
  /** Read/steer the fight. Used only by test/zone5.mjs. */
  debugState(): {
    phase: number
    hp: number
    maxHp: number
    plated: number
    banked: number
    meals: number
    stage: Stage
    piles: { x: number; y: number; gold: number; alive: boolean; grand: boolean; eaten: number }[]
    chandeliers: { x: number; y: number; state: number; t: number }[]
    sweepState: number
    grabT: number
    tookDamage: boolean
    glances: number
    segs: { x: number; y: number }[]
  } {
    return {
      phase: this.phase,
      hp: this.hp,
      maxHp: this.maxHp,
      plated: this.plated,
      banked: this.banked,
      meals: this.meals,
      stage: this.stage,
      piles: this.piles.map((p) => ({ x: p.x, y: p.y, gold: p.gold, alive: p.alive, grand: p.grand, eaten: p.eaten })),
      chandeliers: this.chandeliers.map((c) => ({ x: c.x, y: c.y, state: c.state, t: c.t })),
      sweepState: this.sweepState,
      grabT: this.grabT,
      tookDamage: this.tookDamage,
      glances: this.glances,
      segs: this.segs.map((s) => ({ x: s.x, y: s.y })),
    }
  }

  /** Put a pile under the head so a spec can watch exactly one meal. */
  debugFeed(): void {
    this.target = -1
    for (let i = 0; i < this.piles.length; i++) this.piles[i].alive = false
    this.addPile(this.pos.x + 30, this.pos.y, D.piles.gold, false)
  }

  /** Clear the floor and lay one pile exactly here (the Warden's own race). */
  debugPileAt(x: number, y: number): void {
    this.target = -1
    for (let i = 0; i < this.piles.length; i++) this.piles[i].alive = false
    this.addPile(x, y, D.piles.gold, false)
  }

  debugSetHp(n: number): void {
    this.hp = Math.max(1, Math.min(this.maxHp, n))
  }
}

const dirScratch: Vec = { x: 0, y: 0 }

let live: Hoard | null = null

// ===========================================================================
// sprites
// ===========================================================================

function pstr(p: Record<string, number | string>, k: string, d: string): string {
  const v = p[k]
  return typeof v === 'string' ? v : d
}
function pnum(p: Record<string, number | string>, k: string, d: number): number {
  const v = p[k]
  return typeof v === 'number' ? v : d
}

/**
 * The head. Drawn pointing UP so the rotation in renderHead reads naturally: a
 * wedge of fused coin with a crown fused into the skull and a maw of minted
 * teeth. `open` swings the jaw wide — the pose it wears to swallow and to bite.
 */
registerPainter('z5_hoard_head', (a: PixelArt, p) => {
  const s = a.size
  const cx = s / 2
  const open = pnum(p, 'open', 0) > 0
  const gold = pstr(p, 'gold', '#e3b23c')
  const hot = shade(gold, 0.4)
  const lo = shade(gold, -0.45)
  const dark = '#241206'

  // the skull: a long wedge, widest at the jaw hinge
  for (let y = 0; y < RD(s * 0.55); y++) {
    const k = y / (s * 0.55)
    const w = RD(s * (0.1 + 0.28 * k))
    a.hline(RD(cx - w), RD(s * 0.2 + y), w * 2, y % 7 === 6 ? lo : gold)
  }
  a.ellipse(cx, s * 0.72, s * 0.34, s * 0.16, gold)
  a.ellipse(cx, s * 0.66, s * 0.24, s * 0.1, hot)

  // the crown fused along the brow — crownwork, not decoration
  for (let i = -2; i <= 2; i++) {
    const x = RD(cx + i * s * 0.11)
    const h = RD(s * (i % 2 === 0 ? 0.14 : 0.09))
    a.vline(x, RD(s * 0.2) - h, h, hot)
    a.set(x, RD(s * 0.2) - h - 1, '#ffffff')
  }
  a.hline(RD(cx - s * 0.26), RD(s * 0.2), RD(s * 0.52), hot)

  // eyes: two struck coins, and nothing behind them
  a.disc(cx - s * 0.14, s * 0.36, s * 0.07, dark)
  a.disc(cx + s * 0.14, s * 0.36, s * 0.07, dark)
  a.disc(cx - s * 0.14, s * 0.36, s * 0.045, '#ff4a4a')
  a.disc(cx + s * 0.14, s * 0.36, s * 0.045, '#ff4a4a')

  // the maw
  const jawY = open ? s * 0.62 : s * 0.72
  a.rect(RD(cx - s * 0.26), RD(jawY), RD(s * 0.52), RD(open ? s * 0.3 : s * 0.12), dark)
  if (open) {
    a.rect(RD(cx - s * 0.22), RD(jawY + s * 0.04), RD(s * 0.44), RD(s * 0.2), '#8a1f1f')
    for (let i = 0; i < 7; i++) {
      const x = RD(cx - s * 0.24 + i * s * 0.08)
      a.vline(x, RD(jawY), 4, '#fff2c4')
      a.vline(x + 2, RD(jawY + s * 0.26), 4, '#fff2c4')
    }
  } else {
    for (let i = 0; i < 7; i++) {
      const x = RD(cx - s * 0.24 + i * s * 0.08)
      a.vline(x, RD(jawY), 3, '#fff2c4')
    }
  }
  // coin scales down the neck
  for (let i = 0; i < 4; i++) {
    a.ellipse(cx - s * 0.2 + i * s * 0.13, s * 0.88, s * 0.06, s * 0.04, i % 2 === 0 ? hot : lo)
  }
})

/**
 * One coil. SOLID, not a ring: the segments are spaced closer than their own
 * diameter so they overlap into one body, and anything with a hole in the
 * middle turns that body into a chain of doughnuts. The shading is a tube —
 * bright along the upper-left, dark along the lower-right — so a run of
 * overlapping coils reads as a rounded length of serpent.
 */
registerPainter('z5_hoard_seg', (a: PixelArt, p) => {
  const s = a.size
  const cx = s / 2
  const gold = pstr(p, 'gold', '#e3b23c')
  const hot = shade(gold, 0.42)
  const lo = shade(gold, -0.5)
  const mid = shade(gold, -0.18)

  a.disc(cx, cx, s * 0.48, lo)
  a.disc(cx, cx, s * 0.44, mid)
  a.disc(cx - s * 0.04, cx - s * 0.05, s * 0.36, gold)
  a.disc(cx - s * 0.08, cx - s * 0.1, s * 0.2, hot)
  // struck coin faces pressed into the plating, so the coil reads as money
  for (let i = 0; i < 5; i++) {
    const ang = (i / 5) * TAU + 0.4
    a.ring(cx + Math.cos(ang) * s * 0.26, cx + Math.sin(ang) * s * 0.26, s * 0.09, i % 2 === 0 ? lo : hot)
  }
  a.set(RD(cx - s * 0.14), RD(cx - s * 0.18), '#ffffff')
})

registerPainter('z5_hoardpile', (a: PixelArt, p) => {
  const s = a.size
  const cx = s / 2
  const gold = pstr(p, 'gold', '#e3b23c')
  const hot = shade(gold, 0.42)
  const lo = shade(gold, -0.45)
  a.ellipse(cx, s * 0.78, s * 0.44, s * 0.14, lo)
  a.ellipse(cx, s * 0.7, s * 0.4, s * 0.13, gold)
  a.ellipse(cx - s * 0.14, s * 0.58, s * 0.2, s * 0.09, gold)
  a.ellipse(cx + s * 0.16, s * 0.6, s * 0.18, s * 0.08, shade(gold, -0.16))
  a.ellipse(cx, s * 0.48, s * 0.2, s * 0.09, gold)
  a.ellipse(cx, s * 0.45, s * 0.11, s * 0.045, hot)
  a.vline(RD(cx + s * 0.22), RD(s * 0.34), RD(s * 0.14), hot)
  a.ellipse(cx + s * 0.22, s * 0.32, s * 0.07, s * 0.05, hot)
  a.set(RD(cx - s * 0.1), RD(s * 0.44), '#ffffff')
})

registerPainter('z5_chandelier', (a: PixelArt, p) => {
  const s = a.size
  const cx = s / 2
  const gold = pstr(p, 'gold', '#e3b23c')
  const hot = shade(gold, 0.45)
  const lo = shade(gold, -0.4)

  // chain
  for (let y = 0; y < RD(s * 0.2); y += 4) a.rect(RD(cx - 1), RD(y), 3, 3, lo)
  // three tiers of a gilded ring, hung with candles
  for (let tier = 0; tier < 3; tier++) {
    const y = s * (0.28 + tier * 0.17)
    const r = s * (0.2 + tier * 0.11)
    a.ring(cx, y, r, tier % 2 === 0 ? gold : hot)
    a.ring(cx, y, r - 2, lo)
    const n = 5 + tier * 2
    for (let i = 0; i < n; i++) {
      const ang = (i / n) * TAU
      const x = cx + Math.cos(ang) * r
      a.vline(RD(x), RD(y - s * 0.08), RD(s * 0.07), '#f2e6c8')
      a.set(RD(x), RD(y - s * 0.09), '#ffd166')
    }
  }
  a.disc(cx, s * 0.78, s * 0.07, hot)
})

registerRecipe('z5_hoard_head', { key: 'z5_hoard_head', size: 60, params: { open: 0 } })
registerRecipe('z5_hoard_head_open', { key: 'z5_hoard_head', size: 60, params: { open: 1 } })
registerRecipe('z5_hoard_seg', { key: 'z5_hoard_seg', size: 30 })
registerRecipe('z5_hoardpile', { key: 'z5_hoardpile', size: 30 })
registerRecipe('z5_chandelier', { key: 'z5_chandelier', size: 48 })

// ---------------------------------------------------------------- registry

registerBossArena(D.id, ZONE5_BOSS_ARENA)
registerBoss(D.id, () => {
  live = new Hoard()
  return live
})

/**
 * TEST SEAM (test/zone5.mjs). The fight's state is piles, plates and a banked
 * sum — none of which Snapshot can carry. Inert unless something calls it.
 */
export const boss5Debug = {
  state: (): ReturnType<Hoard['debugState']> | null => (live ? live.debugState() : null),
  feed: (): void => live?.debugFeed(),
  pileAt: (x: number, y: number): void => live?.debugPileAt(x, y),
  setHp: (n: number): void => live?.debugSetHp(n),
}
